import { skipToken } from '@tanstack/react-query'
import { trpc } from '@/main'
import { useClientOnlyQuery } from './use-client-only-query'

type AddressOption = { value: string; label: string }

// Network-backed because the ISO-3166-2 table is 130 KB, and no route owns them — hence useClientOnlyQuery.
export function useCountries(): AddressOption[] {
  // query: direct a reference list no route owns — read by shared form fields across a dozen routes
  return useClientOnlyQuery(trpc.common.getCountries.queryOptions()).data ?? []
}

// Mirrors the priority list in report market-filters: ES first, then LATAM plus BR, US, PT and ZA.
const PRIORITY_CODES = [
  'ES',
  'AR',
  'BO',
  'BR',
  'CL',
  'CO',
  'CR',
  'EC',
  'US',
  'GT',
  'MX',
  'PA',
  'PY',
  'PE',
  'PT',
  'PR',
  'DO',
  'ZA',
  'UY',
  'VE',
]

/** {@link useCountries} split into the popular group (Spain pinned first) and the rest, each sorted by label. */
export function useCountryGroups(): { priority: AddressOption[]; other: AddressOption[] } {
  const options = useCountries()
  const prioritySet = new Set(PRIORITY_CODES)
  const collator = new Intl.Collator()
  const [pinned] = PRIORITY_CODES
  const priority = options
    .filter((o) => prioritySet.has(o.value))
    .sort((a, b) => {
      if (a.value === pinned) return -1
      if (b.value === pinned) return 1
      return collator.compare(a.label, b.label)
    })
  const other = options.filter((o) => !prioritySet.has(o.value)).sort((a, b) => collator.compare(a.label, b.label))
  return { priority, other }
}

/** Provinces of `country` — see {@link useCountries}. Fetches nothing until a country is selected. */
export function useProvinces(country: string | null | undefined): AddressOption[] {
  // query: direct keyed on a country that only exists in form state — see useCountries
  return useClientOnlyQuery(trpc.common.getProvinces.queryOptions(country || skipToken)).data ?? []
}
